/**
 * wc-gift-send.js: the gift card page's "Send gift card" popup, made a real
 * dialog (session GC1, 2026-10-10; Luke: "improve the styling yourself,
 * including accessibility considerations").
 *
 * WHAT THE APP DOES. Go Gift Cards is an app EMBED (settings_data block
 * 6544724514127332554, cdn.shopify.com/extensions/.../go-gift-cards.js). Its
 * script CLONES the first `form[action="/cart/add"] [type="submit"]`, which is
 * Horizon's add-to-cart button, renames the copy "Send gift card"
 * ([data-send-as-a-gift], .ggc-button-1) and, with its `hide_cart_buttons`
 * setting on, hides every other button in the form. Pressing the copy opens a
 * popup: a SHELL in this page's DOM (.send-gift-card-modal, its overlay, two
 * nested containers, a <span> holding the close cross, a spinner) around a
 * cross-origin IFRAME (giftcards.goappstudio.com) that holds the form. Its
 * submit posts {id, properties} to /cart/add.js and goes to checkout.
 *
 * WHAT WAS WRONG, and is fixed here from the theme side:
 * 1. The copy keeps the original's `on:click="/handleClick"` and
 *    `ref="addToCartButton"`. Horizon's delegated listener runs on document in
 *    the CAPTURE phase, so it reaches the copy before the app's own listener
 *    can stop it: pressing "Send gift card" played Horizon's "added to cart"
 *    tick (and fly-to-cart, where on) although nothing was added. And with two
 *    elements on one non-array ref the last wins, so Horizon's enable/disable
 *    of "the" add-to-cart button went to the copy. Both attributes are
 *    removed from the COPY; Horizon's MutationObserver then points the ref
 *    back at the real button.
 * 2. The shell was not a dialog: no role, no name, a close cross no keyboard
 *    could reach, no Escape, focus left behind on the page (which stayed
 *    tabbable underneath), and nothing returned focus on close.
 * 3. Its scroll lock (body.disabled-scroll, position: fixed) jumped a phone
 *    to the top of the page and did not lock Horizon's own desktop scroller,
 *    .page-wrapper. Horizon's html[scroll-lock] does both (base.css).
 *
 * WHAT THIS NEVER DOES: change a value, a field, the app's settings, its
 * iframe or its submission. It only adds attributes (role, aria-*, tabindex,
 * inert, scroll-lock), moves focus, and presses the app's OWN close control
 * for Escape. What only the vendor can fix (inside the iframe) is listed in
 * ~/business-context/wc/theme-reviews/gc1/.
 *
 * Loaded by snippets/wc-head-gc1 on the go-gift-card template only.
 */

const TRIGGER = "[data-send-as-a-gift]";
const ROOT = ".send-gift-card-modal";
const CLOSE = ".send-gift-card-modal__close";
const OVERLAY = ".send-gift-card-modal__overlay";
const FRAME = ".send-gift-card-modal__iframe";
const LOADING = ".send-gift-card-modal__loading";

/** Labels come from the theme's locale (snippets/wc-head-gc1), never typed here. */
function labels() {
  const el = document.querySelector("script[data-wc-gift-send]");
  try {
    return { close: "Close", ...JSON.parse(el?.textContent || "{}") };
  } catch {
    return { close: "Close" };
  }
}

/**
 * The copy is the app's; the attributes removed are Horizon's, carried over by
 * cloneNode. Exported for the offline test.
 * @param {HTMLElement} button
 */
export function fixTrigger(button) {
  if (button.dataset.wcGiftSend) return;
  button.dataset.wcGiftSend = "1";
  button.removeAttribute("on:click");
  button.removeAttribute("ref");
  button.removeAttribute("data-testid");
  button.setAttribute("aria-haspopup", "dialog");
  markSendOnly(button);
}

/**
 * With `hide_cart_buttons` on, the app hides Add to cart and the quantity's
 * minus and plus, and its own add ignores the quantity (it posts {id,
 * properties}): a bare number box that does nothing. Hide the box only when
 * the app really hid Add to cart, so turning that setting off in the app
 * brings both back together.
 * @param {HTMLElement} button
 */
function markSendOnly(button) {
  const buttons = button.closest(".product-form-buttons");
  const original = buttons?.querySelector('[type="submit"]:not([data-send-as-a-gift])');
  if (!buttons || !original) return;
  const hidden = getComputedStyle(original).display === "none";
  buttons.toggleAttribute("data-wc-gift-send-only", hidden);
}

/** @type {{ root: HTMLElement, trigger: HTMLElement | null, inerted: Element[], locked: boolean, watch: MutationObserver } | null} */
let open = null;
/** The last "Send gift card" pressed: where focus returns. */
let lastTrigger = /** @type {HTMLElement | null} */ (null);

/** Every sibling of every ancestor of `root`, up to <body>: the page behind. */
function pageBehind(root) {
  const out = [];
  for (let node = root; node && node !== document.body; node = node.parentElement) {
    const parent = node.parentElement;
    if (!parent) break;
    for (const sib of parent.children) {
      if (sib === node || sib.hasAttribute("inert")) continue;
      if (/^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(sib.tagName)) continue;
      out.push(sib);
    }
  }
  return out;
}

/** @param {HTMLElement} root */
function decorate(root) {
  if (root.dataset.wcGiftSend) return;
  root.dataset.wcGiftSend = "1";
  const text = labels();
  const trigger = lastTrigger && lastTrigger.isConnected ? lastTrigger : document.querySelector(TRIGGER);
  const name = (trigger?.textContent || "").trim();

  root.setAttribute("role", "dialog");
  root.setAttribute("aria-modal", "true");
  if (name) root.setAttribute("aria-label", name);
  root.setAttribute("tabindex", "-1");

  root.querySelector(OVERLAY)?.setAttribute("aria-hidden", "true");

  const close = /** @type {HTMLElement | null} */ (root.querySelector(CLOSE));
  if (close) {
    close.setAttribute("role", "button");
    close.setAttribute("tabindex", "0");
    close.setAttribute("aria-label", text.close);
    for (const svg of close.querySelectorAll("svg")) {
      svg.setAttribute("aria-hidden", "true");
      svg.setAttribute("focusable", "false");
    }
    close.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        close.click();
      }
    });
  }

  // The form is in a cross-origin frame, which the app titles "Gift Card
  // Modal"; it gets the dialog's own name. The dialog is busy until the app
  // shows the form (it keeps the frame `visibility: hidden` while its own
  // spinner turns).
  const frame = /** @type {HTMLIFrameElement | null} */ (root.querySelector(FRAME));
  if (frame && name) frame.setAttribute("title", name);
  syncBusy(root);

  // Tab past the end of the form lands here, and wraps to the close cross:
  // the page behind is inert, so without this it would leave for the
  // browser's own toolbar first.
  if (frame && close) {
    const wrap = document.createElement("span");
    wrap.className = "wc-gc-send__wrap";
    wrap.tabIndex = 0;
    wrap.addEventListener("focus", () => close.focus());
    frame.after(wrap);
  }

  const inerted = pageBehind(root);
  for (const el of inerted) el.setAttribute("inert", "");
  const html = document.documentElement;
  const locked = !html.hasAttribute("scroll-lock");
  if (locked) html.setAttribute("scroll-lock", "");

  // The app flips the frame's inline visibility when its form has loaded.
  const watch = new MutationObserver(() => syncBusy(root));
  if (frame) watch.observe(frame, { attributes: true, attributeFilter: ["style"] });
  watch.observe(root, { childList: true, subtree: true });

  open = { root, trigger, inerted, locked, watch };
  root.focus({ preventScroll: true });
}

/** @param {HTMLElement} root */
function syncBusy(root) {
  const frame = /** @type {HTMLIFrameElement | null} */ (root.querySelector(FRAME));
  const loading = !!root.querySelector(LOADING) || (frame ? frame.style.visibility === "hidden" : false);
  if (loading) root.setAttribute("aria-busy", "true");
  else root.removeAttribute("aria-busy");
  const spinner = root.querySelector(`${LOADING} svg`);
  if (spinner) {
    spinner.setAttribute("aria-hidden", "true");
    // The spinner turns by SMIL (<animateTransform>), which no CSS can stop.
    if (matchMedia("(prefers-reduced-motion: reduce)").matches)
      /** @type {SVGSVGElement} */ (spinner).pauseAnimations?.();
  }
}

/** Undo everything decorate() added to the page, and give focus back. */
function release() {
  if (!open) return;
  const { trigger, inerted, locked, watch } = open;
  open = null;
  watch.disconnect();
  for (const el of inerted) el.removeAttribute("inert");
  if (locked) document.documentElement.removeAttribute("scroll-lock");
  const back = trigger && trigger.isConnected ? trigger : document.querySelector(TRIGGER);
  if (back instanceof HTMLElement) back.focus({ preventScroll: true });
}

function scan() {
  for (const button of document.querySelectorAll(TRIGGER)) fixTrigger(/** @type {HTMLElement} */ (button));
  // A shell already decorated may still be fading out when the next opens.
  if (!open) {
    const fresh = [...document.querySelectorAll(ROOT)].find(
      (el) => !(/** @type {HTMLElement} */ (el).dataset.wcGiftSend),
    );
    if (fresh) decorate(/** @type {HTMLElement} */ (fresh));
  }
  // The app removes the shell after its fade; release() has normally run
  // already (close, overlay, Escape), and this catches any other way out.
  if (open && !open.root.isConnected) release();
}

export function start() {
  // Before the app's own capture listener on the copy runs, so the trigger is
  // known when the shell appears a moment later.
  document.addEventListener(
    "click",
    (event) => {
      const target = /** @type {Element} */ (event.target);
      const trigger = target?.closest?.(TRIGGER);
      if (trigger) lastTrigger = /** @type {HTMLElement} */ (trigger);
      if (open && target?.closest?.(`${CLOSE}, ${OVERLAY}`)) queueMicrotask(release);
    },
    true,
  );
  document.addEventListener(
    "keydown",
    (event) => {
      if (!open || event.key !== "Escape") return;
      const close = /** @type {HTMLElement | null} */ (open.root.querySelector(CLOSE));
      if (!close) return;
      event.preventDefault();
      close.click();
    },
    true,
  );
  new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
  scan();
}

if (typeof document !== "undefined" && !globalThis.__wcGiftSendNoStart) {
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start, { once: true });
  else start();
}
